import { z } from 'zod';

// src/palettes.ts
var PALETTE_IDS = ["cyberpunk", "synthwave", "matrix", "aurora", "ember"];
var DEFAULT_PALETTE = "cyberpunk";
var PALETTE_SLOTS = 6;
var o = (l, c, h) => ({ l: l / 100, c, h });
var neutral = (byte) => {
  const value = byte / 255;
  const linear = value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  return { l: Math.cbrt(linear), c: 0, h: 0 };
};
var PALETTES = {
  cyberpunk: {
    id: "cyberpunk",
    name: "Cyberpunk",
    description: "Cyan, magenta and acid green over midnight blue.",
    slots: [
      o(85, 0.15, 200),
      o(78, 0.2, 350),
      o(85, 0.19, 150),
      o(90, 0.17, 100),
      o(78, 0.16, 300),
      o(80, 0.17, 55)
    ]
  },
  synthwave: {
    id: "synthwave",
    name: "Synthwave",
    description: "Hot pink, electric purple and sunset gold.",
    slots: [
      o(75, 0.22, 350),
      o(70, 0.22, 305),
      o(82, 0.14, 230),
      o(88, 0.15, 165),
      o(88, 0.16, 90),
      o(76, 0.18, 30)
    ]
  },
  matrix: {
    id: "matrix",
    name: "Matrix",
    description: "Phosphor greens with a teal glow.",
    slots: [
      o(87, 0.22, 145),
      o(82, 0.14, 190),
      o(92, 0.2, 120),
      o(94, 0.08, 150),
      o(85, 0.16, 80),
      o(68, 0.2, 25)
    ]
  },
  aurora: {
    id: "aurora",
    name: "Aurora",
    description: "Teal, violet and green like the northern lights.",
    slots: [
      o(85, 0.14, 185),
      o(76, 0.17, 295),
      o(86, 0.18, 150),
      o(80, 0.16, 345),
      o(78, 0.15, 250),
      o(88, 0.15, 95)
    ]
  },
  ember: {
    id: "ember",
    name: "Ember",
    description: "Orange, red and gold with a cool cyan counterpoint.",
    slots: [
      o(80, 0.18, 50),
      o(70, 0.21, 25),
      o(88, 0.16, 90),
      o(75, 0.2, 345),
      o(84, 0.13, 205),
      o(90, 0.18, 125)
    ]
  }
};
var ACCENT_COLOR_IDS = [
  "lime",
  "white",
  "silver",
  "red",
  "orange",
  "yellow",
  "green",
  "cyan",
  "blue",
  "violet",
  "pink"
];
var DEFAULT_ACCENT_COLOR = "blue";
var ACCENT_COLORS = {
  lime: o(85, 0.2, 125),
  white: neutral(242),
  silver: o(82, 5e-3, 260),
  red: o(65, 0.24, 25),
  orange: o(75, 0.19, 55),
  yellow: o(88, 0.18, 100),
  green: o(85, 0.19, 150),
  cyan: o(85, 0.15, 200),
  blue: o(75, 0.18, 250),
  violet: o(78, 0.16, 300),
  pink: o(78, 0.2, 350)
};
var BASE_COLORS = {
  bg: neutral(10),
  surface1: neutral(26),
  surface2: neutral(34),
  border: neutral(64),
  text: neutral(242),
  textMuted: neutral(176),
  warn: o(85, 0.17, 85),
  danger: o(70, 0.2, 20)
};
var LIGHT_BASE_COLORS = {
  bg: neutral(245),
  surface1: neutral(255),
  surface2: neutral(245),
  border: neutral(192),
  text: neutral(32),
  textMuted: neutral(85),
  warn: o(38, 0.13, 70),
  danger: o(42, 0.19, 25)
};
var FILL_TEXT = neutral(10);
function isPaletteId(value) {
  return typeof value === "string" && PALETTE_IDS.includes(value);
}
function getPalette(id) {
  return PALETTES[isPaletteId(id) ? id : DEFAULT_PALETTE];
}
function isAccentColorId(value) {
  return typeof value === "string" && ACCENT_COLOR_IDS.includes(value);
}
function resolveAccentColor(id) {
  return ACCENT_COLORS[isAccentColorId(id) ? id : DEFAULT_ACCENT_COLOR];
}
function slotColor(palette, index) {
  const i = (Math.trunc(index) % PALETTE_SLOTS + PALETTE_SLOTS) % PALETTE_SLOTS;
  return palette.slots[i];
}
var round = (n, dp) => Number(n.toFixed(dp));
function oklchToCss({ l, c, h }, alpha) {
  const a = alpha === void 0 ? "" : ` / ${round(alpha, 3)}`;
  return `oklch(${round(l * 100, 1)}% ${round(c, 3)} ${round(h, 1)}${a})`;
}
function oklchToLinearRgb({ l, c, h }) {
  const hr = h * Math.PI / 180;
  const a = c * Math.cos(hr);
  const b = c * Math.sin(hr);
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.291485548 * b;
  const L = l_ ** 3;
  const M = m_ ** 3;
  const S = s_ ** 3;
  return [
    4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
    -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
    -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S
  ];
}
var clamp01 = (v) => Math.min(1, Math.max(0, v));
var gamma = (v) => v <= 31308e-7 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
function oklchToRgb(color) {
  return oklchToLinearRgb(color).map((v) => Math.round(gamma(clamp01(v)) * 255));
}
function oklchToHex(color) {
  return `#${oklchToRgb(color).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}
function oklchToInt(color) {
  const [r, g, b] = oklchToRgb(color);
  return r << 16 | g << 8 | b;
}
function relativeLuminance(color) {
  const [r, g, b] = oklchToLinearRgb(color).map(clamp01);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}
function paletteCssVars(palette) {
  const vars = {};
  palette.slots.forEach((slot, i) => {
    vars[`--neon-${i + 1}`] = oklchToCss(slot);
    vars[`--neon-${i + 1}-rgb`] = oklchToRgb(slot).join(" ");
  });
  return vars;
}
function accentCssVars(id) {
  const color = ACCENT_COLORS[id];
  return {
    "--accent": oklchToCss(color),
    "--accent-rgb": oklchToRgb(color).join(" ")
  };
}
function stylesheetFor(ids, attr, varsFor) {
  return ids.map((id) => {
    const body = Object.entries(varsFor(id)).map(([k, v]) => `  ${k}: ${v};`).join("\n");
    return `[${attr}="${id}"] {
${body}
}`;
  }).join("\n\n");
}
function palettesStylesheet() {
  return stylesheetFor(PALETTE_IDS, "data-palette", (id) => paletteCssVars(PALETTES[id]));
}
function accentColorsStylesheet() {
  return stylesheetFor(ACCENT_COLOR_IDS, "data-accent", accentCssVars);
}
function themeStylesheet() {
  return `${palettesStylesheet()}

${accentColorsStylesheet()}`;
}

// src/notification-events.ts
var EVENT_GROUPS = [
  {
    id: "budget",
    label: "Budget & monthly plan",
    icon: "\u{1F4CA}",
    color: 3900150,
    destination: "month"
  },
  {
    id: "recurring",
    label: "Recurring expenses",
    icon: "\u{1F501}",
    color: 9133302,
    destination: "bills"
  },
  { id: "cards", label: "Cards & statements", icon: "\u{1F4B3}", color: 959977, destination: "cards" },
  { id: "accounts", label: "Accounts", icon: "\u{1F3E7}", color: 440020, destination: "accounts" },
  {
    id: "loans",
    label: "Loans & repayments",
    icon: "\u{1F3E6}",
    color: 16096779,
    destination: "borrowings"
  },
  {
    id: "savings",
    label: "Deposits & savings goals",
    icon: "\u{1F331}",
    color: 1096065,
    destination: "wealth"
  },
  {
    id: "investments",
    label: "Investments",
    icon: "\u{1F4C8}",
    color: 1357990,
    destination: "investments"
  },
  {
    id: "family",
    label: "Family & access",
    icon: "\u{1F465}",
    color: 11032055,
    destination: "settings?section=family"
  },
  {
    id: "template",
    label: "Budget templates",
    icon: "\u{1F5C2}\uFE0F",
    color: 6583435,
    destination: "template"
  }
];
var ACTIVITY_EVENTS = [
  {
    id: "account_created",
    group: "accounts",
    label: "Account added",
    description: "An account is added in FinBob.",
    method: "POST",
    path: "accounts"
  },
  {
    id: "account_updated",
    group: "accounts",
    label: "Account updated",
    description: "An account is updated in FinBob.",
    method: "PATCH",
    path: "accounts/:id"
  },
  {
    id: "account_deleted",
    group: "accounts",
    label: "Account removed",
    description: "An account is removed in FinBob.",
    method: "DELETE",
    path: "accounts/:id"
  },
  {
    id: "recurring_created",
    group: "recurring",
    label: "Recurring expense added",
    description: "A recurring expense is added in FinBob.",
    method: "POST",
    path: "recurring"
  },
  {
    id: "recurring_updated",
    group: "recurring",
    label: "Recurring expense updated",
    description: "A recurring expense is updated in FinBob.",
    method: "PATCH",
    path: "recurring/:id"
  },
  {
    id: "recurring_deleted",
    group: "recurring",
    label: "Recurring expense removed",
    description: "A recurring expense is removed in FinBob.",
    method: "DELETE",
    path: "recurring/:id"
  },
  {
    id: "emi_created",
    group: "cards",
    label: "Card EMI added",
    description: "A card emi is added in FinBob.",
    method: "POST",
    path: "emis"
  },
  {
    id: "emi_updated",
    group: "cards",
    label: "Card EMI updated",
    description: "A card emi is updated in FinBob.",
    method: "PATCH",
    path: "emis/:id"
  },
  {
    id: "emi_deleted",
    group: "cards",
    label: "Card EMI removed",
    description: "A card emi is removed in FinBob.",
    method: "DELETE",
    path: "emis/:id"
  },
  {
    id: "loan_created",
    group: "loans",
    label: "Loan added",
    description: "A loan is added in FinBob.",
    method: "POST",
    path: "loans"
  },
  {
    id: "loan_updated",
    group: "loans",
    label: "Loan updated",
    description: "A loan is updated in FinBob.",
    method: "PATCH",
    path: "loans/:id"
  },
  {
    id: "loan_deleted",
    group: "loans",
    label: "Loan removed",
    description: "A loan is removed in FinBob.",
    method: "DELETE",
    path: "loans/:id"
  },
  {
    id: "handloan_created",
    group: "loans",
    label: "Personal loan added",
    description: "A personal loan is added in FinBob.",
    method: "POST",
    path: "hand-loans"
  },
  {
    id: "handloan_updated",
    group: "loans",
    label: "Personal loan updated",
    description: "A personal loan is updated in FinBob.",
    method: "PATCH",
    path: "hand-loans/:id"
  },
  {
    id: "handloan_deleted",
    group: "loans",
    label: "Personal loan removed",
    description: "A personal loan is removed in FinBob.",
    method: "DELETE",
    path: "hand-loans/:id"
  },
  {
    id: "deposit_created",
    group: "savings",
    label: "Deposit added",
    description: "A deposit is added in FinBob.",
    method: "POST",
    path: "deposits"
  },
  {
    id: "deposit_updated",
    group: "savings",
    label: "Deposit updated",
    description: "A deposit is updated in FinBob.",
    method: "PATCH",
    path: "deposits/:id"
  },
  {
    id: "deposit_deleted",
    group: "savings",
    label: "Deposit removed",
    description: "A deposit is removed in FinBob.",
    method: "DELETE",
    path: "deposits/:id"
  },
  {
    id: "goal_created",
    group: "savings",
    label: "Savings goal added",
    description: "A savings goal is added in FinBob.",
    method: "POST",
    path: "goals"
  },
  {
    id: "goal_updated",
    group: "savings",
    label: "Savings goal updated",
    description: "A savings goal is updated in FinBob.",
    method: "PATCH",
    path: "goals/:id"
  },
  {
    id: "goal_deleted",
    group: "savings",
    label: "Savings goal removed",
    description: "A savings goal is removed in FinBob.",
    method: "DELETE",
    path: "goals/:id"
  },
  {
    id: "investment_created",
    group: "investments",
    label: "Investment added",
    description: "An investment is added in FinBob.",
    method: "POST",
    path: "investments"
  },
  {
    id: "investment_updated",
    group: "investments",
    label: "Investment updated",
    description: "An investment is updated in FinBob.",
    method: "PATCH",
    path: "investments/:id"
  },
  {
    id: "investment_deleted",
    group: "investments",
    label: "Investment removed",
    description: "An investment is removed in FinBob.",
    method: "DELETE",
    path: "investments/:id"
  },
  {
    id: "month_opened",
    group: "budget",
    label: "Month opened",
    description: "Month opened successfully in FinBob.",
    method: "POST",
    path: "months/:period/open"
  },
  {
    id: "month_closed",
    group: "budget",
    label: "Month closed",
    description: "Month closed successfully in FinBob.",
    method: "POST",
    path: "months/:period/close"
  },
  {
    id: "month_reopened",
    group: "budget",
    label: "Month reopened",
    description: "Month reopened successfully in FinBob.",
    method: "POST",
    path: "months/:period/reopen"
  },
  {
    id: "month_setup_completed",
    group: "budget",
    label: "Monthly setup completed",
    description: "Monthly setup completed successfully in FinBob.",
    method: "POST",
    path: "months/:period/setup/complete"
  },
  {
    id: "month_line_added",
    group: "budget",
    label: "Monthly item added",
    description: "Monthly item added in the household budget.",
    method: "POST",
    path: "months/:period/lines"
  },
  {
    id: "month_line_updated",
    group: "budget",
    label: "Monthly item edited",
    description: "Monthly item edited in the household budget.",
    method: "PATCH",
    path: "months/:period/lines/:lineId"
  },
  {
    id: "month_line_removed",
    group: "budget",
    label: "Monthly item removed",
    description: "Monthly item removed in the household budget.",
    method: "DELETE",
    path: "months/:period/lines/:lineId"
  },
  {
    id: "month_line_status_changed",
    group: "budget",
    label: "Payment status changed",
    description: "Payment status changed in the household budget.",
    method: "POST",
    path: "months/:period/lines/:lineId/status"
  },
  {
    id: "buckets_updated",
    group: "budget",
    label: "Budget allocation saved",
    description: "Budget allocation saved in the household budget.",
    method: "PUT",
    path: "buckets"
  },
  {
    id: "recurring_paid",
    group: "recurring",
    label: "Recurring payment recorded",
    description: "Recurring payment recorded successfully in FinBob.",
    method: "POST",
    path: "recurring/:id/mark-paid"
  },
  {
    id: "card_transaction_added",
    group: "cards",
    label: "Card transaction added",
    description: "Card transaction added successfully in FinBob.",
    method: "POST",
    path: "cards/:accountId/periods/:period/transactions"
  },
  {
    id: "card_transaction_updated",
    group: "cards",
    label: "Card transaction edited",
    description: "Card transaction edited successfully in FinBob.",
    method: "PATCH",
    path: "cards/:accountId/transactions/:id"
  },
  {
    id: "card_transaction_removed",
    group: "cards",
    label: "Card transaction removed",
    description: "Card transaction removed successfully in FinBob.",
    method: "DELETE",
    path: "cards/:accountId/transactions/:id"
  },
  {
    id: "card_bulk_updated",
    group: "cards",
    label: "Card classifications updated",
    description: "Card classifications updated successfully in FinBob.",
    method: "PATCH",
    path: "cards/:accountId/transactions/bulk"
  },
  {
    id: "statement_imported",
    group: "cards",
    label: "Statement import committed",
    description: "Statement import committed successfully in FinBob.",
    method: "POST",
    path: "cards/:accountId/imports/:importId/commit"
  },
  {
    id: "statement_removed",
    group: "cards",
    label: "Saved statement removed",
    description: "Saved statement removed successfully in FinBob.",
    method: "DELETE",
    path: "cards/:accountId/statements/:importId"
  },
  {
    id: "loan_payment_recorded",
    group: "loans",
    label: "Loan payment recorded",
    description: "Loan payment recorded successfully in FinBob.",
    method: "POST",
    path: "loans/:id/payments"
  },
  {
    id: "loan_payment_removed",
    group: "loans",
    label: "Loan payment record removed",
    description: "Loan payment record removed successfully in FinBob.",
    method: "DELETE",
    path: "loans/:id/payments/:paymentId"
  },
  {
    id: "loan_rate_changed",
    group: "loans",
    label: "Loan rate change recorded",
    description: "Loan rate change recorded successfully in FinBob.",
    method: "POST",
    path: "loans/:id/rate-changes"
  },
  {
    id: "handloan_repayment_recorded",
    group: "loans",
    label: "Personal repayment recorded",
    description: "Personal repayment recorded successfully in FinBob.",
    method: "POST",
    path: "hand-loans/:id/repayments"
  },
  {
    id: "handloan_repayment_removed",
    group: "loans",
    label: "Personal repayment removed",
    description: "Personal repayment removed successfully in FinBob.",
    method: "DELETE",
    path: "hand-loans/:id/repayments/:repaymentId"
  },
  {
    id: "deposit_contribution_added",
    group: "savings",
    label: "Deposit contribution added",
    description: "Deposit contribution added successfully in FinBob.",
    method: "POST",
    path: "deposits/:id/contributions"
  },
  {
    id: "deposit_contribution_removed",
    group: "savings",
    label: "Deposit contribution removed",
    description: "Deposit contribution removed successfully in FinBob.",
    method: "DELETE",
    path: "deposits/:id/contributions/:contributionId"
  },
  {
    id: "goal_contribution_added",
    group: "savings",
    label: "Goal contribution added",
    description: "Goal contribution added successfully in FinBob.",
    method: "POST",
    path: "goals/:id/contributions"
  },
  {
    id: "goal_contribution_removed",
    group: "savings",
    label: "Goal contribution removed",
    description: "Goal contribution removed successfully in FinBob.",
    method: "DELETE",
    path: "goals/:id/contributions/:contributionId"
  },
  {
    id: "investment_activity_recorded",
    group: "investments",
    label: "Investment movement recorded",
    description: "Investment movement recorded successfully in FinBob.",
    method: "POST",
    path: "investments/:id/events"
  },
  {
    id: "family_profile_added",
    group: "family",
    label: "Family profile added",
    description: "Family profile added successfully in FinBob.",
    method: "POST",
    path: "people"
  },
  {
    id: "family_profile_removed",
    group: "family",
    label: "Family profile removed",
    description: "Family profile removed successfully in FinBob.",
    method: "DELETE",
    path: "people/:personId"
  },
  {
    id: "invitation_created",
    group: "family",
    label: "Invitation created or renewed",
    description: "Invitation created or renewed successfully in FinBob.",
    method: "POST",
    path: "invites"
  },
  {
    id: "invitation_revoked",
    group: "family",
    label: "Invitation revoked",
    description: "Invitation revoked successfully in FinBob.",
    method: "DELETE",
    path: "invites/:inviteId"
  },
  {
    id: "member_removed",
    group: "family",
    label: "Member access removed",
    description: "Member access removed successfully in FinBob.",
    method: "DELETE",
    path: "members/:userId"
  },
  {
    id: "template_line_added",
    group: "template",
    label: "Template item added",
    description: "Template item added successfully in FinBob.",
    method: "POST",
    path: "template/lines"
  },
  {
    id: "template_line_updated",
    group: "template",
    label: "Template item edited",
    description: "Template item edited successfully in FinBob.",
    method: "PATCH",
    path: "template/lines/:id"
  },
  {
    id: "template_line_removed",
    group: "template",
    label: "Template item removed",
    description: "Template item removed successfully in FinBob.",
    method: "DELETE",
    path: "template/lines/:id"
  },
  {
    id: "template_reordered",
    group: "template",
    label: "Template order changed",
    description: "Template order changed successfully in FinBob.",
    method: "POST",
    path: "template/reorder"
  },
  {
    id: "template_starter_applied",
    group: "template",
    label: "Starter template applied",
    description: "Starter template applied successfully in FinBob.",
    method: "POST",
    path: "template/apply-starter"
  }
];
var REMINDER_EVENTS = [
  {
    id: "line_due",
    group: "budget",
    label: "Payment due",
    description: "A planned expense reaches its reminder date."
  },
  {
    id: "unpaid_lines",
    group: "budget",
    label: "Unpaid monthly expenses",
    description: "Expenses remain unpaid after the settle-by day."
  },
  {
    id: "month_not_opened",
    group: "budget",
    label: "Month not opened",
    description: "The scheduled monthly setup day has passed."
  },
  {
    id: "recurring_due",
    group: "recurring",
    label: "Recurring expense due",
    description: "A subscription, insurance premium or recurring bill is due."
  },
  {
    id: "card_statement_day",
    group: "cards",
    label: "Statement day",
    description: "The scheduled bank statement date arrives."
  },
  {
    id: "card_bill_due",
    group: "cards",
    label: "Card bill due",
    description: "A card payment reminder is due."
  },
  {
    id: "emi_last",
    group: "cards",
    label: "Final scheduled EMI",
    description: "The last scheduled card instalment month arrives; this does not confirm payment."
  },
  {
    id: "loan_emi_due",
    group: "loans",
    label: "Loan EMI due",
    description: "A scheduled loan instalment approaches."
  },
  {
    id: "handloan_due",
    group: "loans",
    label: "Personal loan repayment due",
    description: "A repayment to make or receive approaches."
  },
  {
    id: "sip_details_due",
    group: "investments",
    label: "SIP purchase details",
    description: "Monthly reminder to enter purchase date, quantity and price for a SIP."
  },
  {
    id: "ppf_rate_review",
    group: "savings",
    label: "Annual PPF rate review",
    description: "Confirm the PPF rate at the start of each Indian financial year."
  },
  {
    id: "deposit_maturity",
    group: "savings",
    label: "Deposit maturity",
    description: "A deposit approaches its maturity date."
  },
  {
    id: "goal_reached",
    group: "savings",
    label: "Savings goal reached",
    description: "Recorded savings reach the target."
  },
  {
    id: "member_joined",
    group: "family",
    label: "Member joined",
    description: "An invited account accepts household membership."
  }
];
var EVENT_CATALOG = [...REMINDER_EVENTS, ...ACTIVITY_EVENTS];
var CANVAS_PATTERNS = [
  "linen",
  "dots",
  "grid",
  "waves",
  "plain",
  "currency",
  "coins",
  "banknotes"
];
var CANVAS_ANIMATIONS = ["still", "drift", "float", "breathe", "rotate"];
var canvasConfigSchema = z.object({
  placement: z.enum(["rows", "staggered", "diagonal", "scattered", "edges"]).optional(),
  currencies: z.array(z.enum(["INR", "USD", "EUR", "GBP", "JPY", "AED", "CHF", "CAD", "AUD"])).max(9).optional(),
  animation: z.enum(CANVAS_ANIMATIONS),
  speed: z.number().min(0.25).max(3),
  scale: z.number().min(0.5).max(2),
  intensity: z.number().min(0.1).max(1)
});
var canvasSettingsSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    return {
      currency: { animation: "still", speed: 1, scale: 1, intensity: 0.45 },
      coins: { animation: "still", speed: 1, scale: 1, intensity: 0.45 },
      banknotes: { animation: "still", speed: 1, scale: 1, intensity: 0.45 },
      ...value
    };
  },
  z.record(z.enum(CANVAS_PATTERNS), canvasConfigSchema)
);
var DEFAULT_CANVAS_SETTINGS = Object.fromEntries(
  CANVAS_PATTERNS.map((pattern) => [
    pattern,
    {
      animation: "still",
      speed: 1,
      scale: 1,
      intensity: 0.45
    }
  ])
);
var HOUSEHOLD_FONTS = [
  "modern",
  "rounded",
  "classic",
  "editorial",
  "mono",
  "bungee",
  "chewy",
  "marker",
  "pacifico",
  "righteous",
  "lobster"
];
var userPreferencesSchema = z.object({
  palette: z.enum(PALETTE_IDS),
  accentColor: z.enum(ACCENT_COLOR_IDS),
  customAccent: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null),
  canvasSettings: canvasSettingsSchema.default(DEFAULT_CANVAS_SETTINGS),
  canvasPattern: z.enum(CANVAS_PATTERNS).default("linen"),
  householdFont: z.enum(HOUSEHOLD_FONTS).default("modern"),
  householdNameSize: z.number().int().min(14).max(28).default(18),
  reducedGlow: z.boolean(),
  glass: z.boolean(),
  motion: z.enum(["system", "on", "off"]),
  theme: z.enum(["system", "dark", "light"]),
  sound: z.boolean(),
  soundVolume: z.number().min(0).max(1),
  haptics: z.boolean(),
  emailDigest: z.boolean(),
  privacyMode: z.boolean()
});
var userPreferencesPatchSchema = userPreferencesSchema.partial().extend({
  householdFont: z.enum(HOUSEHOLD_FONTS).optional(),
  householdNameSize: z.number().int().min(14).max(28).optional(),
  customAccent: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  canvasSettings: canvasSettingsSchema.optional(),
  // PATCH must not reset the canvas when another preference changes.
  canvasPattern: z.enum(CANVAS_PATTERNS).optional()
});

// src/constants.ts
var LINE_KINDS = ["income", "expense"];
var LINE_STATUSES = ["unpaid", "paid", "skipped"];
var CLASSIFICATIONS = ["essential", "non_essential"];
var CADENCES = ["monthly", "quarterly", "half_yearly", "yearly", "one_time"];
var RECURRING_KINDS = [
  "subscription",
  "insurance",
  "bill",
  "tax",
  "fee",
  "other"
];
var ACCOUNT_TYPES = ["bank", "credit_card", "cash", "wallet"];
var EDIT_SCOPES = ["this_month", "this_and_future"];
var MEMBER_ROLES = ["owner", "member"];
var PERSON_ROLES = ["adult", "child"];
var MONTH_STATUSES = ["open", "closed"];
var RECURRENCE_CADENCES = [
  "monthly",
  "quarterly",
  "half_yearly",
  "yearly",
  "custom"
];
var LOAN_TYPES = ["home", "car", "personal", "education", "other"];
var LOAN_RATE_TYPES = ["fixed", "floating"];
var LOAN_STATUSES = ["active", "closed"];
var LOAN_PAYMENT_KINDS = ["emi", "prepayment"];
var LOAN_PREPAYMENT_MODES = ["reduce_tenure", "reduce_emi"];
var DEPOSIT_TYPES = ["fd", "rd"];
var DEPOSIT_STATUSES = ["active", "matured", "closed"];
var HAND_LOAN_DIRECTIONS = ["given", "taken"];
var HAND_LOAN_STATUSES = ["open", "settled"];
var GOAL_STATUSES = ["active", "achieved", "abandoned"];
var NOTIFICATION_TYPES = [
  "line_due",
  "unpaid_lines",
  "month_not_opened",
  "recurring_due",
  "card_statement_day",
  "card_bill_due",
  "loan_emi_due",
  "handloan_due",
  "deposit_maturity",
  "ppf_rate_review",
  "sip_details_due",
  "emi_last",
  "goal_reached",
  "member_joined",
  ...ACTIVITY_EVENTS.map((event) => event.id)
];
var NOTIFICATION_CHANNELS = ["inapp", "email", "discord", "webpush"];
var EMAIL_DIGEST_MODES = ["immediate", "daily_digest"];
var DEFAULT_CURRENCY = "INR";
var DEFAULT_LOCALE = "en-IN";
var DEFAULT_TIMEZONE = "Asia/Kolkata";
var DEFAULT_BUCKETS = [
  {
    key: "needs",
    name: "Needs",
    targetPct: 50,
    colorIndex: 0,
    absorbsSpillOver: false,
    sortOrder: 0
  },
  {
    key: "wants",
    name: "Wants",
    targetPct: 30,
    colorIndex: 1,
    absorbsSpillOver: false,
    sortOrder: 1
  },
  {
    key: "savings",
    name: "Savings",
    targetPct: 20,
    colorIndex: 2,
    absorbsSpillOver: true,
    sortOrder: 2
  }
];
var DEFAULT_HOUSEHOLD_SETTINGS = {
  monthOpenDay: 1,
  settleByDay: 7
};
var DEFAULT_PREFERENCES = {
  palette: DEFAULT_PALETTE,
  accentColor: DEFAULT_ACCENT_COLOR,
  canvasPattern: "plain",
  customAccent: null,
  canvasSettings: DEFAULT_CANVAS_SETTINGS,
  householdFont: "modern",
  householdNameSize: 18,
  reducedGlow: true,
  glass: false,
  motion: "system",
  // New users start calm and quiet; saved user preferences remain authoritative.
  theme: "system",
  sound: false,
  soundVolume: 0.3,
  haptics: true,
  emailDigest: true,
  privacyMode: false
};

export { ACCENT_COLORS, ACCENT_COLOR_IDS, ACCOUNT_TYPES, ACTIVITY_EVENTS, BASE_COLORS, CADENCES, CANVAS_ANIMATIONS, CANVAS_PATTERNS, CLASSIFICATIONS, DEFAULT_ACCENT_COLOR, DEFAULT_BUCKETS, DEFAULT_CANVAS_SETTINGS, DEFAULT_CURRENCY, DEFAULT_HOUSEHOLD_SETTINGS, DEFAULT_LOCALE, DEFAULT_PALETTE, DEFAULT_PREFERENCES, DEFAULT_TIMEZONE, DEPOSIT_STATUSES, DEPOSIT_TYPES, EDIT_SCOPES, EMAIL_DIGEST_MODES, EVENT_CATALOG, EVENT_GROUPS, FILL_TEXT, GOAL_STATUSES, HAND_LOAN_DIRECTIONS, HAND_LOAN_STATUSES, HOUSEHOLD_FONTS, LIGHT_BASE_COLORS, LINE_KINDS, LINE_STATUSES, LOAN_PAYMENT_KINDS, LOAN_PREPAYMENT_MODES, LOAN_RATE_TYPES, LOAN_STATUSES, LOAN_TYPES, MEMBER_ROLES, MONTH_STATUSES, NOTIFICATION_CHANNELS, NOTIFICATION_TYPES, PALETTES, PALETTE_IDS, PALETTE_SLOTS, PERSON_ROLES, RECURRENCE_CADENCES, RECURRING_KINDS, REMINDER_EVENTS, accentColorsStylesheet, accentCssVars, canvasConfigSchema, canvasSettingsSchema, contrastRatio, getPalette, isAccentColorId, isPaletteId, oklchToCss, oklchToHex, oklchToInt, oklchToRgb, paletteCssVars, palettesStylesheet, relativeLuminance, resolveAccentColor, slotColor, themeStylesheet, userPreferencesPatchSchema, userPreferencesSchema };
//# sourceMappingURL=chunk-CRYUOAYG.js.map
//# sourceMappingURL=chunk-CRYUOAYG.js.map